import { ProgressBar } from '@design-systems/canvas-ui'
import { useState } from 'react'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { useTargetCircleCard } from 'hooks/loyalty/useTargetCircleCard'
import { useEssentials } from 'hooks/utils/useEssentials'
import {
    buildTableDataForTargetCircleCardNonStore,
    pickColumns,
} from './helpers'
import { addHyperlinkToFirstColumn } from 'utils/data'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import { format, parseISO } from 'date-fns'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { messages } from './messages'
import { translateTableData } from 'locales/utils'

export const TargetCircleCardNonStore = () => {
    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)
    const [tabsIndex, setTabsIndex] = useState<number>(0)
    const { currentLocation, logout, env, pathname, session, t } =
        useEssentials()
    const { data, isLoading, isError, error, refetch } = useTargetCircleCard({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    const count = useCountdown(5, logout, error as Error)

    const { loyalty } = TRANSLATION_KEYS
    const { circleCard } = loyalty

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError || !data) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const { recentData, archiveData } = data
    const { current_week, previous_week } = recentData
    const { month_to_date, year_to_date } = archiveData

    const currentWeekNoDataState = !current_week.metrics.all.goal
    const previousWeekNoDataState = !previous_week.metrics.all.goal
    const isNoDataState =
        (timePeriodIndex === 0 && currentWeekNoDataState) ||
        (timePeriodIndex === 1 && previousWeekNoDataState)

    let currentWeekData =
        buildTableDataForTargetCircleCardNonStore(current_week)
    let previousWeekData =
        buildTableDataForTargetCircleCardNonStore(previous_week)
    let monthToDateData =
        buildTableDataForTargetCircleCardNonStore(month_to_date)
    let yearToDateData = buildTableDataForTargetCircleCardNonStore(year_to_date)

    if (!isNoDataState) {
        currentWeekData = addHyperlinkToFirstColumn(
            currentWeekData,
            currentLocation,
            pathname,
        )
        currentWeekData = translateTableData({
            t,
            table: currentWeekData,
            translation: {
                location: circleCard.location,
                total: circleCard.total,
                forecast: circleCard.forecast,
                '% to forecast': circleCard['% to forecast'],
            },
        })

        previousWeekData = addHyperlinkToFirstColumn(
            previousWeekData,
            currentLocation,
            pathname,
        )
        previousWeekData = translateTableData({
            t,
            table: previousWeekData,
            translation: {
                location: circleCard.location,
                total: circleCard.total,
                forecast: circleCard.forecast,
                '% to forecast': circleCard['% to forecast'],
            },
        })
    }
    monthToDateData = addHyperlinkToFirstColumn(
        monthToDateData,
        currentLocation,
        pathname,
    )
    monthToDateData = translateTableData({
        t,
        table: monthToDateData,
        translation: {
            location: circleCard.location,
            total: circleCard.total,
            forecast: circleCard.forecast,
            '% to forecast': circleCard['% to forecast'],
        },
    })

    yearToDateData = addHyperlinkToFirstColumn(
        yearToDateData,
        currentLocation,
        pathname,
    )
    yearToDateData = translateTableData({
        t,
        table: yearToDateData,
        translation: {
            location: circleCard.location,
            total: circleCard.total,
            forecast: circleCard.forecast,
            '% to forecast': circleCard['% to forecast'],
        },
    })

    const dataByTimePeriod = [
        { title: 'week to date', data: currentWeekData },
        { title: 'last week', data: previousWeekData },
        { title: 'month to date', data: monthToDateData },
        { title: 'year to date', data: yearToDateData },
    ]

    const viewInPercent = dataByTimePeriod.map(({ title, data }) => ({
        title,
        subtitle: '',
        data: {
            [t(circleCard.summary)]: pickColumns(data, [0, 1, 2, 3]),
            [t(circleCard.forecast)]: pickColumns(data, [0, 4, 5, 6]),
        },
    }))
    const payload = {
        'view in %': viewInPercent,
    }

    const objectsByTimePeriod = [
        current_week,
        previous_week,
        month_to_date,
        year_to_date,
    ]

    type Metrics = {
        goal: number
        total_applications: number
        variance_percentage: number
    }
    const totals = Object.values(objectsByTimePeriod[timePeriodIndex].metrics)[
        tabsIndex
    ] as Metrics
    const totalApplications = totals.total_applications?.toLocaleString()
    const forecastApplications = totals.goal?.toLocaleString()

    let lastApplication = ''
    if (timePeriodIndex === 0 && !isNoDataState) {
        lastApplication = format(
            parseISO(current_week.last_updated),
            'MMM d, h:mma',
        )
    }
    const completionPercent = (totals.total_applications / totals.goal) * 100

    return (
        <GeneralizedComponent
            payload={payload}
            isLoading={isLoading}
            scrollableWidth={false}
            widths={[90, 90, 90, 100]}
            expandedWidth={false}
            parentHandleClick={(timePeriodIndex: number, tabsIndex: number) => {
                setTimePeriodIndex(timePeriodIndex)
                setTabsIndex(tabsIndex)
            }}
            isNoData={isNoDataState}
            titleClassName={'hc8-fs-150'}
            kpiName="loyalty target circle card non-store"
            isTimePeriodSticky={false}
        >
            <div className="hc8-mv-4x">
                {lastApplication.length > 0 && (
                    <p className="hc8-mb-4x hc8-ta-center">{`Last application received ${lastApplication}`}</p>
                )}
                <p className="flex-sb hc8-mb-2x">
                    <span>{t(circleCard.applications)}</span>
                    <span>
                        {messages.applicationNumbers(
                            totalApplications,
                            forecastApplications,
                        )}
                    </span>
                </p>
                <ProgressBar
                    percentComplete={completionPercent}
                    className={
                        timePeriodIndex > 0 && completionPercent < 100
                            ? 'incomplete'
                            : ''
                    }
                />
            </div>
        </GeneralizedComponent>
    )
}
